import {
  Spine,
  SkeletonJson,
  AtlasAttachmentLoader,
  TextureAtlas,
} from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { formatToCustomLocale } from "../../../utilities";
import { FONT_STYLES } from "../../../fontStyles";
import SpineAnimationData from "../../../utils/SpineAnimationData";

// The skeleton's text bone and colour slot. The spine has been re-exported
// with different names more than once ("basic_win" / "value"), so both are
// tried in order and the first that exists wins — a rename no longer silently
// kills the text.
// const TEXT_BONE_NAMES = ["value", "basic_win"];
const TEXT_BONE_NAMES = ["value", "basic_win", "txt_slot_value"];
const TEXT_SLOT_NAMES = ["txt_slot_value", "txt_slot_value_basic_win"];
// The spine (renamed from winline_text, 2026-10-05) and its skin for now.
const SPINE_NAME = "basic_win";
const SKIN_NAME = "fire";

// Win text configuration - fine tune both global and per-line win text
export const WIN_TEXT_CONFIG = {
  // Font size as multiplier of SYMBOL_HEIGHT (higher = bigger text)
  // GLOBAL_WIN_FONT_SIZE: 0.975, // Font size for "show all wins" phase
  // A bit smaller (2026-10-12): with its fire following it (FIRE_FOLLOWS_TEXT)
  // the fire was stretched ~3x and read rasterized. Fire follows it down.
  GLOBAL_WIN_FONT_SIZE: 0.85, // Font size for "show all wins" phase
  // LINE_WIN_FONT_SIZE: 0.825, // Font size for individual line cycling
  // (Its own literal, NOT x GLOBAL: the global shrinking leaves it as it is.)
  // 35% smaller than the global win (Vladimir, 2026-10-05).
  // LINE_WIN_FONT_SIZE: 0.975 * 0.65, // Font size for individual line cycling
  // A bit smaller again (2026-10-13): it now sits ON the line's middle-reel
  // symbol, and at full size it covered it.
  LINE_WIN_FONT_SIZE: 0.975 * 0.65 * 0.8, // Font size for individual line cycling
  // The font size the fire is drawn 1:1 at (its scale = font size / this). Fixed —
  // the old line size — so changing either font size scales text AND fire.
  FIRE_REF_FONT_SIZE: 0.975 * 0.65,
  // Where in its cell a line's amount sits: this x the cell height BELOW the
  // cell centre — the bottom part of the win box, the symbol left visible.
  // LINE_WIN_Y_IN_CELL: 0.28,
  // LINE_WIN_Y_IN_CELL: 0, // back on the cell centre (2026-10-13)
  LINE_WIN_Y_IN_CELL: 0.4, // further down than 0.28 — on the box's bottom edge (2026-10-13)

  // basic_win's skin per phase (2026-10-05): the "show all wins" total burns,
  // the per-line amounts do not.
  GLOBAL_WIN_SKIN: "fire",
  // LINE_WIN_SKIN: "no_fire",
  // Fire for both (Vladimir, 2026-10-05: "use fire for all of them").
  LINE_WIN_SKIN: "fire",

  // ── Super turbo's flash (2026-10-08) ── the last part of the window the
  // text fades out in (showWinAmount `flashMs`), ms.
  FLASH_FADE_MS: 300,
  // ...and how it comes IN (2026-10-08, Vladimir: "in is somehow wonky"):
  // a pop like Mad Labs' `in` instead of a cut onto the loop — alpha up over
  // FLASH_IN_ALPHA_MS while the scale grows from FROM to a PEAK overshoot and
  // settles at 1 by FLASH_IN_MS.
  FLASH_IN_MS: 220,
  FLASH_IN_ALPHA_MS: 120,
  // A LONGER window (turbo's global win, 2026-10-08: "jumps in too fast and
  // jittery") pops in longer: FLASH_IN_WINDOW_SHARE of it, from FLASH_IN_MS up
  // to FLASH_IN_MAX_MS; its alpha takes FLASH_IN_ALPHA_SHARE of the pop.
  FLASH_IN_WINDOW_SHARE: 0.3,
  FLASH_IN_MAX_MS: 400,
  FLASH_IN_ALPHA_SHARE: 0.55,
  // A frame never moves the pop by more than this (ms): the frame the flash
  // starts on also builds the whole win (boxes, symbols, dim), and its long
  // delta skipped half the pop.
  FLASH_MAX_STEP_MS: 34,
  FLASH_IN_FROM_SCALE: 0.85,
  FLASH_IN_PEAK_SCALE: 1.08,

  // Spine scale multiplier — enlarges the glow effect without affecting text size.
  // Text is counter-scaled by 1/multiplier to stay the same.
  SPINE_SCALE_MULTIPLIER: 2.0, // 1.0 = native, >1 = bigger glow
  // The fire FOLLOWS the text (Vladimir, 2026-10-12: the global win's number
  // was enormous on a small fire, the line win's looked right). The line win
  // is the reference: each instance's fire is SPINE_SCALE_MULTIPLIER x (its
  // font size / LINE_WIN_FONT_SIZE), its text counter-scaled by the same, so
  // the text keeps its size and every win shows the same fire-to-text ratio.
  FIRE_FOLLOWS_TEXT: true,
  // The fire slightly see-through (2026-10-12) — the skeleton's own colour,
  // so every fire layer and none of the number (the text is not a slot).
  // FIRE_ALPHA: 0.85,
  FIRE_ALPHA: 0.9, // less see-through, +5% (2026-10-12)
  // The per-line amounts' fire only (2026-10-13: "a bit more transparent,
  // global remains the same").
  LINE_FIRE_ALPHA: 0.75,
  // The NUMBER alone, x this against its fire (2026-10-12: "text too big
  // compared to the animation", ~15% smaller, fire unchanged). Drawn at its
  // full font size and shown smaller (updateTextFromBone), so it stays crisp.
  TEXT_SCALE: 0.85,

  // Text Y offset relative to bone position (positive = down, negative = up)
  TEXT_Y_OFFSET: 0,

  // ── Contrast on the fire (PO demo feedback, 2026-10-12) ── the cream ->
  // yellow digits with their thin outline melted into the flame behind them.
  // After the PO's reference (not a copy): warmer digits running yellow ->
  // orange, a THICK red gradient outline that holds the edge against the
  // fire (below). Sizes are x the font size, so the
  // global and the line win match. Applied over FONT_STYLES.winText here only
  // — the other winText users (the intro's MAX WIN etc.) are untouched.
  CONTRAST_STYLE: {
    fill: ["#fff8c8", "#ffdc4a", "#ff9a1c"],
    fillGradientStops: [0, 0.45, 1],
    fillGradientType: 0, // vertical
    stroke: "#d41a1a", // overdrawn by CONTRAST_STROKE_GRADIENT
    lineJoin: "round",
  },
  // CONTRAST_STROKE: 0.18, // PIXI draws half under the fill: ~9% shows
  // CONTRAST_LETTER_SPACING: 0.02, // room for the fatter outline between digits
  CONTRAST_LETTER_SPACING: 0.03, // room for the thicker outline between digits
  // The outline as a red GRADIENT, not one flat brown (Vladimir, 2026-10-12:
  // "way too brown … boring" — then "no brown thing behind, one gradient, not
  // layers"). ONE stroke, top to bottom through these colours, drawn by
  // GradientStrokeText below; `CONTRAST_STROKE` is x the font size (half of it
  // shows past the glyph). No drop shadow.
  // CONTRAST_STROKE: 0.22,
  // CONTRAST_STROKE: 0.19, // a tiny bit thinner (2026-10-12)
  CONTRAST_STROKE: 0.16, // thinner again (2026-10-12)
  // CONTRAST_STROKE_GRADIENT: ["#ff4a2a", "#d41a1a", "#8e0a0a"],
  // CONTRAST_STROKE_GRADIENT: ["#ff4a2a", "#d41a1a", "#700606"], // darker bottom (2026-10-12)
  // CONTRAST_STROKE_GRADIENT: ["#ff4a2a", "#d41a1a", "#520303"], // darker again (2026-10-12)
  // Halfway back to the first flat brown (#6a0500) — "too red", but not that
  // brown either (2026-10-12). The dark bottom kept.
  // CONTRAST_STROKE_GRADIENT: ["#b42715", "#9f0f0d", "#520303"],
  // The logo's own reds (logo.webp, 2026-10-12): the HOLD & WIN panel's red
  // at the top, the plate's warm red-brown in the middle, the CLEOPATRA'S
  // letters' dark brown outline at the bottom.
  // CONTRAST_STROKE_GRADIENT: ["#c00500", "#8d3516", "#5f240a"],
  // A bit less brown — the middle and bottom toward the panel's red (2026-10-12).
  // CONTRAST_STROKE_GRADIENT: ["#c00500", "#9a1e0e", "#5a1306"],
  // A tiny bit more red, like the HOLD & WIN plate (2026-10-12).
  // CONTRAST_STROKE_GRADIENT: ["#c00500", "#a4120a", "#640a05"],
  // The GRAND jackpot plate's own red (game_view land_jckp_grand, 2026-10-12):
  // pure red, top to bottom.
  // CONTRAST_STROKE_GRADIENT: ["#e90202", "#bc0202", "#950202"],
  // CONTRAST_STROKE_GRADIENT: ["#e90202", "#bc0202", "#7a0202"], // darker bottom
  CONTRAST_STROKE_GRADIENT: ["#e90202", "#bc0202", "#620202"], // darker bottom again
};

/**
 * A PIXI.Text whose STROKE is a vertical gradient (WIN_TEXT_CONFIG.
 * CONTRAST_STROKE_GRADIENT). PIXI's style only takes one stroke colour, but
 * every stroke goes through drawLetterSpacing(…, isStroke), so the gradient is
 * set on the context there. One text, so the outline can never drift off the
 * digits. Assumes no dropShadow: its pass strokes in black through the same
 * call.
 */
class GradientStrokeText extends PIXI.Text {
  constructor(text, style, strokeColors) {
    super(text, style);
    this._strokeColors = strokeColors;
    this._strokeGradient = null;
    this._strokeGradientH = 0;
  }

  drawLetterSpacing(text, x, y, isStroke = false) {
    if (isStroke && this._strokeColors?.length) {
      // From the glyph block's top to its bottom (the canvas minus padding),
      // in the context's units (it is already scaled by the resolution).
      const pad = this._style.padding || 0;
      const h = this.canvas.height / this._resolution;
      if (!this._strokeGradient || this._strokeGradientH !== h) {
        const g = this.context.createLinearGradient(0, pad, 0, h - pad);
        const last = this._strokeColors.length - 1;
        this._strokeColors.forEach((color, i) => g.addColorStop(last ? i / last : 0, color));
        this._strokeGradient = g;
        this._strokeGradientH = h;
      }
      this.context.strokeStyle = this._strokeGradient;
    }
    super.drawLetterSpacing(text, x, y, isStroke);
  }
}

/**
 * The board's win number look (WIN_TEXT_CONFIG.CONTRAST_*) at `fontSize`: the
 * yellow -> orange digits with the red gradient outline. Shared with the
 * fireball counter under the reels (GameViewSimpleController).
 * @param {string} text
 * @param {number} fontSize
 * @returns {PIXI.Text}
 */
export const createWinNumberText = (text, fontSize) => {
  const c = WIN_TEXT_CONFIG;
  const style = new PIXI.TextStyle({
    ...FONT_STYLES.winText,
    ...c.CONTRAST_STYLE,
    fontSize,
    strokeThickness: Math.round(fontSize * c.CONTRAST_STROKE),
    letterSpacing: Math.round(fontSize * c.CONTRAST_LETTER_SPACING),
    padding: Math.round(fontSize * 0.25),
    dropShadow: false,
  });
  return new GradientStrokeText(text, style, c.CONTRAST_STROKE_GRADIENT);
};

/**
 * WinTextController - Unified controller for both global and line win text
 * Uses winline_text spine with txt bone for text attachment
 * Configurable font size allows it to serve both purposes
 */
class WinTextController {
  // constructor(container, symbolWidth, symbolHeight, fontSizeMultiplier = 0.25) {
  constructor(container, symbolWidth, symbolHeight, fontSizeMultiplier = 0.25, skinName = SKIN_NAME) {
    this.container = container;
    // basic_win skin — WIN_TEXT_CONFIG.GLOBAL_WIN_SKIN / LINE_WIN_SKIN.
    this.skinName = skinName || SKIN_NAME;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.fontSizeMultiplier = fontSizeMultiplier; // 0.25 for line wins, 0.35 for global wins

    // Spine instance
    this.winlineSpine = null;
    this.initialized = false;
    this.isCleanedUp = false;

    // Text container attached to spine bone
    this.textContainer = null;
    this.textBitmap = null;
    this.boneUpdateTicker = null;

    // Internal cache for performance optimization
    this._scaleCache = null;
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;

    this.initializeSpine();
  }

  /**
   * Check if controller is ready
   */
  isReady() {
    return this.winlineSpine?.state && this.initialized && !this.isCleanedUp;
  }

  /**
   * Get the spine instance for event listening
   */
  getSpineInstance() {
    return this.winlineSpine;
  }

  /**
   * Initialize the winline_text spine
   */
  initializeSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (!assets.has(`${SPINE_NAME}-skeleton`)) {
        return;
      }

      const skeletonData = PIXI.Assets.get(`${SPINE_NAME}-skeleton`);
      if (!skeletonData) {
        return;
      }

      if (assets.has(`${SPINE_NAME}-atlas`)) {
        this.winlineSpine = Spine.from({
          skeleton: `${SPINE_NAME}-skeleton`,
          atlas: `${SPINE_NAME}-atlas`,
        });
      } else {
        // TEXTURELESS skeleton — no atlas, no page image. Spine.from() cannot
        // build one, so the data is parsed by hand against an EMPTY atlas.
        // That is safe here precisely because the skeleton has no attachments
        // at all: nothing ever asks the atlas for a region. Everything visible
        // is the PIXI.Text pinned to the bone below.
        this.winlineSpine = this._createTexturelessSpine(skeletonData);
      }

      if (
        !this.winlineSpine ||
        !this.winlineSpine.skeleton ||
        !this.winlineSpine.state
      ) {
        return;
      }

      // The skin (basic_win: fire / no_fire).
      if (this.winlineSpine.skeleton.data.findSkin(this.skinName)) {
        this.winlineSpine.skeleton.setSkinByName(this.skinName);
        this.winlineSpine.skeleton.setSlotsToSetupPose();
      }
      // The fire a touch see-through (WIN_TEXT_CONFIG.FIRE_ALPHA). Setup poses
      // never reset the skeleton's own colour, so this holds.
      // this.winlineSpine.skeleton.color.a = WIN_TEXT_CONFIG.FIRE_ALPHA ?? 1;
      // The per-line amounts' fire more see-through than the global's
      // (2026-10-13) — the instance at LINE_WIN_FONT_SIZE is the line one.
      const isLine = this.fontSizeMultiplier === WIN_TEXT_CONFIG.LINE_WIN_FONT_SIZE;
      this.winlineSpine.skeleton.color.a =
        (isLine ? WIN_TEXT_CONFIG.LINE_FIRE_ALPHA : WIN_TEXT_CONFIG.FIRE_ALPHA) ??
        WIN_TEXT_CONFIG.FIRE_ALPHA ??
        1;

      // Scale spine proportionally to current screen size
      this.applySpineScaling();

      // Position at center
      this.winlineSpine.x = 0;
      this.winlineSpine.y = 0;
      this.winlineSpine.zIndex = 20;
      this.winlineSpine.alpha = 0.001; // Invisible but stays in GPU pipeline

      this.container.addChild(this.winlineSpine);

      // Initialize text attached to bone
      this.initializeText();

      this.initialized = true;

      // Warm up spine to prevent first-use jitter
      this.warmUpSpine();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Build a Spine from skeleton data alone, with no atlas.
   *
   * PIXI caches the .json as a plain object, so it is re-parsed through
   * SkeletonJson against an empty TextureAtlas. Returns null on failure rather
   * than throwing — the caller treats a missing spine as "no win text".
   */
  _createTexturelessSpine(skeletonJson) {
    try {
      const parser = new SkeletonJson(
        new AtlasAttachmentLoader(new TextureAtlas("")),
      );
      const data = parser.readSkeletonData(skeletonJson);
      return new Spine(data);
    } catch (error) {
      return null;
    }
  }

  /**
   * First bone/slot that actually exists on this skeleton, by name.
   */
  _findTextBone() {
    const skeleton = this.winlineSpine?.skeleton;
    if (!skeleton) return null;
    for (const name of TEXT_BONE_NAMES) {
      const bone = skeleton.findBone(name);
      if (bone) return bone;
    }
    return null;
  }

  _findTextSlot() {
    const skeleton = this.winlineSpine?.skeleton;
    if (!skeleton) return null;
    for (const name of TEXT_SLOT_NAMES) {
      const slot = skeleton.findSlot(name);
      if (slot) return slot;
    }
    return null;
  }

  /**
   * Hide smoke effect by removing attachments from smoke-related slots
   * NOTE: Disabled — full spine animation now plays including all effects
   */
  hideSmokeEffect() {
    // No-op: full animation enabled
  }

  /**
   * Warm up spine by setting animation and updating transforms
   * This ensures spine is fully ready before first use (prevents jitter)
   */
  warmUpSpine() {
    if (!this.winlineSpine?.state) return;

    try {
      // Play "in" animation briefly to populate spine structure and GPU buffers
      this.winlineSpine.state.setAnimation(0, "in", false);
      this.winlineSpine.state.update(0.016);

      // Populate bone hierarchy for GPU
      try {
        this.winlineSpine.skeleton.updateWorldTransform();
      } catch (e) {
        // Physics not initialized — safe to continue
      }

      // Clear tracks and reset for clean first use
      // NOTE: Do NOT call setToSetupPose() — causes DarkSlotMesh memory leak in spine-pixi-v7
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
    } catch (error) {
      // Silent fail - warmup is optional optimization
    }
  }

  /**
   * Apply responsive scaling to match current screen size.
   * At design resolution (max width) the spine renders at native size (scale=1.0).
   * On smaller screens it shrinks proportionally, preserving aspect ratio.
   */
  /**
   * The fire's multiplier for THIS instance (WIN_TEXT_CONFIG.FIRE_FOLLOWS_TEXT):
   * SPINE_SCALE_MULTIPLIER, grown with the font past the line win's.
   */
  _fireScale() {
    const base = WIN_TEXT_CONFIG.SPINE_SCALE_MULTIPLIER || 1;
    if (!WIN_TEXT_CONFIG.FIRE_FOLLOWS_TEXT) return base;
    // const ref = WIN_TEXT_CONFIG.LINE_WIN_FONT_SIZE || this.fontSizeMultiplier || 1;
    // A FIXED reference (2026-10-13): relative to LINE_WIN_FONT_SIZE, shrinking
    // the line amount left its fire as it was and grew the global's fire.
    // Text and fire now scale together, for both.
    const ref = WIN_TEXT_CONFIG.FIRE_REF_FONT_SIZE || this.fontSizeMultiplier || 1;
    return base * ((this.fontSizeMultiplier || ref) / ref);
  }

  applySpineScaling() {
    if (!this.winlineSpine) return;

    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const widthScale = this.symbolWidth / nativeWidth;
    const heightScale = this.symbolHeight / nativeHeight;
    const scaleRatio = Math.min(widthScale, heightScale);

    // Apply multiplier to enlarge glow — text is counter-scaled in updateTextFromBone
    // const finalScale = scaleRatio * (WIN_TEXT_CONFIG.SPINE_SCALE_MULTIPLIER || 1);
    const finalScale = scaleRatio * this._fireScale();
    this.winlineSpine.scale.set(finalScale);
    // The layout's scale — the flash's pop scales around it (_startFlash).
    this._spineBaseScale = finalScale;
  }

  /**
   * Initialize text container and attach to spine bone
   */
  initializeText() {
    if (!this.winlineSpine || !this.winlineSpine.skeleton) {
      return;
    }

    if (!this._findTextBone()) {
      return;
    }

    // Create text container as child of spine
    this.textContainer = new PIXI.Container();
    this.textContainer.name = "winline_text_attached_to_bone";
    this.textContainer.alpha = 0;
    this.textContainer.zIndex = 1000;

    this.winlineSpine.addChild(this.textContainer);
    this.winlineSpine.sortableChildren = true;

    const fontSize = Math.floor(
      Settings.SYMBOL_HEIGHT * this.fontSizeMultiplier,
    );

    // const textStyle = new PIXI.TextStyle({
    //   ...FONT_STYLES.winText,
    //   fontSize: fontSize,
    // });
    // More contrast on the fire — WIN_TEXT_CONFIG.CONTRAST_STYLE (2026-10-12).
    // const textStyle = new PIXI.TextStyle({
    //   ...FONT_STYLES.winText,
    //   fontSize: fontSize,
    //   ...c.CONTRAST_STYLE,
    //   strokeThickness: Math.round(fontSize * c.CONTRAST_STROKE),
    //   dropShadowDistance: Math.round(fontSize * c.CONTRAST_SHADOW_DISTANCE),
    //   dropShadowBlur: Math.round(fontSize * c.CONTRAST_SHADOW_BLUR),
    //   letterSpacing: Math.round(fontSize * c.CONTRAST_LETTER_SPACING),
    //   padding: Math.round(fontSize * 0.25),
    // });
    //
    // this.textBitmap = new PIXI.Text("", textStyle);
    // One text, its outline a red gradient (CONTRAST_STROKE_GRADIENT).
    this.textBitmap = createWinNumberText("", fontSize);

    this.textBitmap.anchor.set(0.5);
    this.textBitmap.x = 0;
    this.textBitmap.y = 0;
    this.textBitmap.alpha = 0.001;

    this.textContainer.addChild(this.textBitmap);
  }

  /**
   * Update text display with new value
   */
  updateTextDisplay(value) {
    if (!this.textBitmap) return;

    // Format value - numbers only, no currency symbol
    const formattedValue = formatToCustomLocale(Math.floor(value));

    // Update the text value
    this.textBitmap.text = formattedValue;

    // Auto-shrink if text overflows the spine's native width
    // Uses the skeleton bounding box as the max width constraint
    this.textBitmap.scale.set(1); // Reset before measuring
    // const maxWidth = this.winlineSpine?.skeleton?.data?.width || 0;
    // // OFF (2026-10-05): winline_text had NO width, so this never applied.
    // // basic_win's is its fire art's bounds (278) — every amount was squeezed
    // // to it, the global and the line win to the SAME width, so the line win's
    // // smaller font did nothing.
    // const maxWidth = 0;
    // BACK, per phase (Vladimir, 2026-10-05: the global win "as it is", the
    // line win smaller). The global win keeps the skeleton's width as its cap;
    // the line win's cap shrinks with its font (x LINE / GLOBAL = 0.65), so it
    // reads 35% smaller digit for digit, capped or not.
    const capRatio = Math.min(
      1,
      this.fontSizeMultiplier / (WIN_TEXT_CONFIG.GLOBAL_WIN_FONT_SIZE || 1),
    );
    const maxWidth = (this.winlineSpine?.skeleton?.data?.width || 0) * capRatio;
    if (maxWidth > 0 && this.textBitmap.width > maxWidth) {
      const scale = maxWidth / this.textBitmap.width;
      this.textBitmap.scale.set(scale);
    }
  }

  /**
   * Sync text container transform with spine's txt_winline_value bone
   * Called every frame while spine is animating
   */
  updateTextFromBone() {
    if (
      !this.winlineSpine ||
      !this.textContainer ||
      !this.winlineSpine.skeleton
    ) {
      return;
    }

    const textBone = this._findTextBone();
    if (!textBone) {
      return;
    }

    // Apply bone's world POSITION (needed for animated text tracking)
    // but use LOCAL scale to avoid double-scaling — textContainer already
    // inherits spine's own scale as a child. Using worldScale would apply
    // spine scale twice, crushing the text raster and causing pixelation.
    // Counter-scale by 1/SPINE_SCALE_MULTIPLIER so text stays at original size.
    // const counterScale = 1 / (WIN_TEXT_CONFIG.SPINE_SCALE_MULTIPLIER || 1);
    // const counterScale = 1 / this._fireScale();
    const counterScale = (WIN_TEXT_CONFIG.TEXT_SCALE ?? 1) / this._fireScale();
    this.textContainer.x = textBone.worldX;
    this.textContainer.y = textBone.worldY;
    this.textContainer.rotation =
      (textBone.getWorldRotationX() * Math.PI) / 180;
    this.textContainer.scale.set(
      textBone.scaleX * counterScale,
      textBone.scaleY * counterScale,
    );

    // Apply slot's color alpha to text container (for fade-in/out animation)
    const textSlot = this._findTextSlot();
    if (textSlot && textSlot.color) {
      this.textContainer.alpha = textSlot.color.a;
    }
  }

  /**
   * Start syncing text with spine bone every frame
   */
  startBoneUpdateLoop() {
    if (!this.winlineSpine || !this.textContainer) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.winlineSpine && this.winlineSpine.alpha > 0.01) {
          this.updateTextFromBone();
        }
      };
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing text with spine bone
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Show win amount with spine animation.
   * Plays "in" → "idle" (once) → "out". Caller controls lifecycle via hide().
   * @param {number} amount - Win amount to display
   * @param {Object} [options] - Optional config
   * @param {boolean} [options.skipIntro=false] - Skip "in" phase, play idle→out directly (super turbo)
   * @param {number} [options.targetDuration] - If set, auto-scales playback to fit in→idle→out in this many ms
   * @param {number} [options.flashMs] - Super turbo: show at once, burning at
   *   its own speed, and FADE out by the end of this many ms (see _startFlash)
   */
  // showWinAmount(amount, { skipIntro = false, targetDuration } = {}) {
  showWinAmount(amount, { skipIntro: skipIntroOption = false, targetDuration: targetOption, flashMs } = {}) {
    if (!this.isReady()) return;

    // ── Super turbo flashes, it does not fast-forward (Vladimir, 2026-10-08:
    // "we sped up the animation insted of fading it out") ── squeezing
    // basic_win's in -> loop -> out (2.33 s) into the 700 ms window played
    // its fire and smoke at 3.3x. Mad Labs' winline_text was mostly alpha,
    // so the same squeeze read as a fade; this one does not. A flash skips
    // `in`, loops at native speed and fades the whole text out instead.
    const flash = flashMs > 0;
    // ── Room for the spine's OWN `in`? (2026-10-08: "it has a weird bounce at
    // entrance") ── the pop's overshoot stacked on `loop`'s own text pulse
    // (1 -> 1.2 -> 1) — two bumps. When the window holds `in` + the fade at
    // native speed (turbo's ~1 s global win), the real entrance plays instead
    // and the pop is not used; a shorter window (super turbo's 700 ms) keeps
    // the pop.
    const inAnimation = this.winlineSpine.skeleton?.data?.findAnimation("in");
    const flashFadeMs = Math.min(WIN_TEXT_CONFIG.FLASH_FADE_MS || 300, (flashMs || 0) / 2);
    const flashWithIntro =
      flash && !!inAnimation && inAnimation.duration * 1000 + flashFadeMs <= flashMs;
    const skipIntro = flash || skipIntroOption;
    const targetDuration = flash ? undefined : targetOption;
    this._cancelFlash();

    // Cancel any pending out-animation resolve
    this._cancelPendingOut();

    // Step 1: Ensure invisible during setup (alpha-only, no visible toggle)
    this.winlineSpine.alpha = 0.001;

    // Step 2: Update text while invisible
    this.updateTextDisplay(amount);

    // Step 3: Reset spine animation state
    // NOTE: Do NOT call setToSetupPose() — causes DarkSlotMesh memory leak in spine-pixi-v7
    if (this.winlineSpine.state) {
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
      this.winlineSpine.state.timeScale = 1; // Reset from any previous speed-up
    }

    // Step 3b: Apply timeScale if targetDuration is set — speeds up the full
    // in→idle→out chain to fit within the requested duration.
    if (targetDuration && targetDuration > 0 && this.winlineSpine.state) {
      const nativeDuration = this._getAnimationChainDuration(skipIntro);
      if (nativeDuration > 0 && nativeDuration > targetDuration) {
        this.winlineSpine.state.timeScale = nativeDuration / targetDuration;
      }
    }

    // Step 3b: Reset text slot alpha — clearTracks() doesn't reset slot colors.
    // After "out" animation fades slot alpha to 0, it stays 0 unless "in" re-animates it.
    // When skipIntro skips "in", the stale alpha=0 makes text invisible.
    const textSlot = this._findTextSlot();
    if (textSlot?.color) {
      textSlot.color.a = 1;
    }
    // ...and EVERY slot's colour, not just the text's (2026-10-05). basic_win's
    // `out` fades the fire (logo_fire_00000) to 0 and no clip keys it back, so
    // from the second win on the fire stayed invisible. Colours only — not
    // setToSetupPose (see the DarkSlotMesh note above); attachments are put
    // back by the AnimationState itself.
    for (const slot of this.winlineSpine.skeleton?.slots || []) {
      if (slot.color && slot.data?.color) slot.color.setFromColor(slot.data.color);
    }

    // Step 4: Chain animation sequence
    if (this.winlineSpine.state) {
      if (flashWithIntro) {
        // Its own entrance at its own speed, then burning until the fade.
        this.winlineSpine.state.setAnimation(0, "in", false);
        this.winlineSpine.state.addAnimation(0, this._idleClip(), true, 0);
      } else if (flash) {
        // The flash: the loop, burning, until the fade has taken it.
        this.winlineSpine.state.setAnimation(0, this._idleClip(), true);
      } else if (skipIntro) {
        // Super turbo: skip "in" fade — appear at full opacity immediately
        this.winlineSpine.state.setAnimation(0, this._idleClip(), false);
        this.winlineSpine.state.addAnimation(0, "out", false, 0);
      } else {
        // Normal: full cycle "in" → "idle" → "out"
        this.winlineSpine.state.setAnimation(0, "in", false);
        this.winlineSpine.state.addAnimation(0, this._idleClip(), false, 0);
        this.winlineSpine.state.addAnimation(0, "out", false, 0);
      }
      this.winlineSpine.state.apply(this.winlineSpine.skeleton);
      this.winlineSpine.state.update(0.016);
      this.winlineSpine.state.apply(this.winlineSpine.skeleton);
    }

    // Step 5: Start bone update loop (will run on next ticker frame)
    this.startBoneUpdateLoop();

    // Step 6: Reveal
    if (skipIntro) {
      // Super turbo: reveal SYNCHRONOUSLY — no RAF defer.
      // Prevents race where hide() from resetAllEffects cancels the pending RAF.
      this.updateTextFromBone();
      this.winlineSpine.alpha = 1;
      if (this.textBitmap) {
        this.textBitmap.alpha = 1;
      }
      // if (flash) this._startFlash(flashMs);
      if (flash) this._startFlash(flashMs, { pop: !flashWithIntro });
    } else {
      // Normal: reveal on NEXT frame after ticker has updated world transforms
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (!this.winlineSpine || this.winlineSpine.destroyed) return;

        // Sync text with bone AFTER ticker has updated world transforms
        this.updateTextFromBone();

        // Make spine visible — spine's slot animation drives textContainer.alpha
        this.winlineSpine.alpha = 1;
        // Ensure text bitmap is renderable (slot alpha controls actual visibility)
        if (this.textBitmap) {
          this.textBitmap.alpha = 1;
        }
      });
    }
  }

  /**
   * Play the "out" animation for a smooth natural transition.
   * Returns a Promise that resolves when "out" finishes.
   * The spine auto-hides and state is fully cleared after "out" completes,
   * so the next showWinAmount() starts a fresh in→idle cycle.
   *
   * If hide() is called during "out", the promise resolves immediately
   * and the hard kill takes precedence.
   */
  playOut() {
    return new Promise((resolve) => {
      if (!this.isReady() || !this.winlineSpine?.state) {
        resolve();
        return;
      }

      // ── A FLASH is its own out (2026-10-08) ── it fades and hides itself
      // at the end of its window; `out` started over it would fight its fade.
      // Wait for it: its hide() resolves every waiter (_cancelPendingOut).
      if (this._flashTicker) {
        const previous = this._outResolve;
        this._outResolve = () => {
          previous?.();
          resolve();
        };
        return;
      }

      // Already playing its `out` (2026-10-05): the cycle start and the first
      // line both ask for it a moment apart — the second must not restart it.
      // It waits on the same `complete`.
      const current = this.winlineSpine.state.getCurrent(0);
      // if (current?.animation?.name === "out" && this._outResolve) {
      //   const previous = this._outResolve;
      //   this._outResolve = () => {
      //     previous();
      //     resolve();
      //   };
      //   return;
      // }
      // ...and the chain's OWN `out` (in -> loop -> out queued by
      // showWinAmount) counts too: never replayed. Finished — just tidy up;
      // still running — wait for its `complete`.
      if (current?.animation?.name === "out") {
        if (current.isComplete()) {
          this._finalizeOut();
          resolve();
          return;
        }
        const previous = this._outResolve;
        this._outResolve = () => {
          previous?.();
          resolve();
        };
        if (!previous) {
          this.winlineSpine.state.addListener({
            complete: (entry) => {
              if (entry.animation?.name !== "out") return;
              this._finalizeOut();
              const done = this._outResolve;
              this._outResolve = null;
              done?.();
            },
          });
        }
        return;
      }

      // Store resolve so hide() can cancel it
      this._outResolve = resolve;

      // Clear current animation and listeners, then play "out"
      this.winlineSpine.state.clearListeners();
      this.winlineSpine.state.setAnimation(0, "out", false);

      // Listen for "out" animation complete
      this.winlineSpine.state.addListener({
        complete: (entry) => {
          if (entry.animation?.name === "out") {
            // "out" finished naturally — clean up and resolve
            this._finalizeOut();
            // resolve();
            // Every waiter (see the guard above), not just the first.
            const done = this._outResolve || resolve;
            this._outResolve = null;
            done();
          }
        },
      });
    });
  }

  /**
   * Cancel any pending playOut() promise.
   * Called by hide() and showWinAmount() to prevent stale resolves.
   */
  _cancelPendingOut() {
    if (this._outResolve) {
      this._outResolve();
      this._outResolve = null;
    }
  }

  /**
   * Internal cleanup after "out" animation finishes naturally.
   * Hides spine, clears text, stops bone loop — identical end state to hide().
   */
  _finalizeOut() {
    if (!this.winlineSpine) return;

    this.stopBoneUpdateLoop();

    this.winlineSpine.alpha = 0.001;

    if (this.textBitmap) {
      this.textBitmap.text = "";
      this.textBitmap.alpha = 0.001;
    }
    if (this.textContainer) {
      this.textContainer.alpha = 0.001;
    }

    // Clear spine state so next showWinAmount() starts fresh
    if (this.winlineSpine.state) {
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
    }
  }

  /**
   * Hard kill — immediately hide at any stage (in/idle/out).
   * Used when user presses spin. Clears all state so next
   * showWinAmount() starts a fresh in→idle→out cycle.
   */
  /**
   * The flash's fade: full for the window minus FLASH_FADE_MS (at most half of
   * it), then the whole spine — fire, smoke and amount, the text is its child —
   * to nothing, then hide(). On the ticker (the timer rule); hide() and the
   * next show cancel it.
   */
  // `pop` false: the spine's own `in` is the entrance — full alpha and the
  // layout's scale from the first frame, only the fade at the end.
  _startFlash(totalMs, { pop = true } = {}) {
    const cfg = WIN_TEXT_CONFIG;
    const fadeMs = Math.min(cfg.FLASH_FADE_MS || 300, totalMs / 2);
    const holdMs = totalMs - fadeMs;
    // The pop-in, never longer than what is left before the fade.
    // const inMs = Math.min(cfg.FLASH_IN_MS || 220, holdMs);
    // const alphaMs = Math.min(cfg.FLASH_IN_ALPHA_MS || 120, inMs);
    // Scaled with the window: super turbo's 700 ms keeps its 220 / 120 (the
    // pop he approved); turbo's longer window pops in longer, not faster.
    const inMs = !pop
      ? 0
      : Math.min(
          Math.max(cfg.FLASH_IN_MS || 220, totalMs * (cfg.FLASH_IN_WINDOW_SHARE ?? 0.3)),
          cfg.FLASH_IN_MAX_MS || 400,
          holdMs,
        );
    const alphaMs = Math.min(
      Math.max(cfg.FLASH_IN_ALPHA_MS || 120, inMs * (cfg.FLASH_IN_ALPHA_SHARE ?? 0.55)),
      inMs,
    );
    const from = cfg.FLASH_IN_FROM_SCALE ?? 0.85;
    const peak = cfg.FLASH_IN_PEAK_SCALE ?? 1.08;
    // The overshoot lands at ~65% of the pop, then settles.
    const peakMs = inMs * 0.65;
    const base = this._spineBaseScale || this.winlineSpine.scale.x || 1;
    const easeOutCubic = (x) => 1 - (1 - x) ** 3;
    const easeInOutSine = (x) => -(Math.cos(Math.PI * x) - 1) / 2;
    const scaleAt = (ms) => {
      if (ms >= inMs) return 1;
      if (ms < peakMs) return from + (peak - from) * easeOutCubic(ms / peakMs);
      return peak + (1 - peak) * easeInOutSine((ms - peakMs) / (inMs - peakMs));
    };
    let elapsed = 0;
    const pose = () => {
      const spine = this.winlineSpine;
      // The layout's scale NOW — a resize mid-flash moves it (layout above).
      const layoutScale = this._spineBaseScale || base;
      // Pop: alpha up, scale through its overshoot.
      if (elapsed < inMs) {
        spine.alpha = Math.max(0.001, Math.min(1, elapsed / alphaMs));
        spine.scale.set(layoutScale * scaleAt(elapsed));
        return;
      }
      spine.scale.set(layoutScale);
      if (elapsed < holdMs) {
        spine.alpha = 1;
        return;
      }
      // Fade. Never below the hidden alpha the rest of this class uses.
      const t = Math.min(1, (elapsed - holdMs) / fadeMs);
      spine.alpha = Math.max(0.001, 1 - t);
      if (t >= 1) this.hide();
    };
    // The first frame posed NOW — not one frame at full size.
    pose();
    this._flashBase = base;
    this._flashTicker = () => {
      if (!this.winlineSpine || this.winlineSpine.destroyed) {
        this._cancelFlash();
        return;
      }
      // elapsed += PIXI.Ticker.shared.deltaMS;
      // Steady: one heavy frame cannot skip the pop forward.
      elapsed += Math.min(PIXI.Ticker.shared.deltaMS, cfg.FLASH_MAX_STEP_MS || 34);
      pose();
    };
    PIXI.Ticker.shared.add(this._flashTicker);
  }

  _cancelFlash() {
    if (!this._flashTicker) return;
    PIXI.Ticker.shared.remove(this._flashTicker);
    this._flashTicker = null;
    // Cut mid-pop: the layout's scale back.
    if (this._flashBase && this.winlineSpine && !this.winlineSpine.destroyed) {
      this.winlineSpine.scale.set(this._spineBaseScale || this._flashBase);
    }
    this._flashBase = null;
  }

  hide() {
    if (!this.winlineSpine) return;

    // A flash still fading stops here (super turbo).
    this._cancelFlash();

    // Resolve any pending playOut() promise immediately
    this._cancelPendingOut();

    // Cancel any pending show
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Immediate alpha kill — no "out" animation
    this.winlineSpine.alpha = 0.001;

    // Clear text
    if (this.textBitmap) {
      this.textBitmap.text = "";
      this.textBitmap.alpha = 0.001;
    }
    if (this.textContainer) {
      this.textContainer.alpha = 0.001;
    }

    // Clean spine state — fully reset so next show starts fresh from "in"
    // NOTE: Do NOT call setToSetupPose() — causes DarkSlotMesh memory leak in spine-pixi-v7
    if (this.winlineSpine.state) {
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
      this.winlineSpine.state.timeScale = 1; // Reset speed-up for next use
    }
  }

  /**
   * Get total native duration of the animation chain (in→idle→out or idle→out).
   * Used to auto-compute timeScale for targetDuration.
   * @param {boolean} skipIntro - Whether "in" phase is skipped
   * @returns {number} Total duration in ms
   */
  /** The hold between in and out: `idle` (winline_text) or `loop` (basic_win). */
  _idleClip() {
    return this.winlineSpine?.skeleton?.data?.findAnimation("idle") ? "idle" : "loop";
  }

  _getAnimationChainDuration(skipIntro = false) {
    // Use pre-cached durations from SpineAnimationData (extracted once at init)
    const cached = SpineAnimationData.getWinTextChainDuration(skipIntro);
    if (cached > 0) return cached;

    // Fallback: read from live spine data if SpineAnimationData not yet initialized
    if (!this.winlineSpine?.skeleton?.data?.animations) return 0;

    const animations = this.winlineSpine.skeleton.data.animations;
    const getAnimDuration = (name) => {
      const anim = animations.find((a) => a.name === name);
      return anim ? anim.duration * 1000 : 0;
    };

    const inDuration = skipIntro ? 0 : getAnimDuration("in");
    // const idleDuration = getAnimDuration("idle");
    const idleDuration = getAnimDuration(this._idleClip());
    const outDuration = getAnimDuration("out");

    return inDuration + idleDuration + outDuration;
  }

  /**
   * Update dimensions when symbol size changes
   */
  updateDimensions(newSymbolWidth, newSymbolHeight) {
    if (
      this.symbolWidth === newSymbolWidth &&
      this.symbolHeight === newSymbolHeight
    ) {
      return;
    }

    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;

    if (this.winlineSpine) {
      this.applySpineScaling();
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.isCleanedUp = true;

    // Cancel any pending show rAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // A flash still fading must not tick on a torn-down spine.
    this._cancelFlash();

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // ── Cleanup text — guarded, because we may be SECOND ──
    //
    // When this component is inside a subtree React unmounts, @pixi/react has
    // already destroyed the parent Container with `children: true` during the
    // mutation phase. Passive cleanups like this one run AFTER that, so by now
    // these two are usually destroyed already, and PIXI's Sprite.destroy()
    // does `this._texture.off(...)` on a null texture — the crash seen on the
    // way out of the bonus room, which then took the whole tree down with it.
    //
    // The spine below has always been guarded this way. The text was not.
    if (this.textBitmap) {
      if (!this.textBitmap.destroyed) this.textBitmap.destroy();
      this.textBitmap = null;
    }

    if (this.textContainer) {
      if (!this.textContainer.destroyed) {
        if (this.textContainer.parent) {
          this.textContainer.parent.removeChild(this.textContainer);
        }
        this.textContainer.destroy({ children: true });
      }
      this.textContainer = null;
    }

    // Cleanup spine
    if (this.winlineSpine && !this.winlineSpine.destroyed) {
      try {
        if (this.winlineSpine.state) {
          this.winlineSpine.state.clearTracks();
          this.winlineSpine.state.clearListeners();
        }
        if (this.winlineSpine.parent) {
          this.winlineSpine.parent.removeChild(this.winlineSpine);
        }
        this.winlineSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    this.winlineSpine = null;
    this.container = null;
    this.initialized = false;
  }
}

export default WinTextController;
